import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationCenterService } from 'src/app/services/notification-center/notification-center.service';
import { NotificationItem, TaskNotification } from 'src/app/services/notification-center/notification-center.types';

@Component({
  selector: 'app-notification-drawer',
  standalone: true,
  imports: [CommonModule, ButtonModule, DrawerModule, TooltipModule],
  templateUrl: './notification-drawer.component.html'
})
export class NotificationDrawerComponent {
  protected readonly expandedTaskIds = signal<Record<string, boolean>>({});
  protected readonly collapsed = signal(false);
  protected readonly notificationCenter = this.notificationCenterService;

  constructor(private readonly notificationCenterService: NotificationCenterService) {}

  protected get drawerVisible(): boolean {
    return this.notificationCenter.drawerOpen();
  }

  protected set drawerVisible(visible: boolean) {
    if (visible) {
      this.notificationCenter.openDrawer();
      return;
    }

    this.notificationCenter.closeDrawer();
  }

  protected toggleExpanded(itemId: string): void {
    this.expandedTaskIds.update((current) => ({
      ...current,
      [itemId]: !current[itemId]
    }));
  }

  protected toggleCollapsed(): void {
    this.collapsed.update((current) => !current);
  }

  protected isExpanded(item: NotificationItem): boolean {
    if (item.type !== 'task') {
      return false;
    }

    const state = this.expandedTaskIds()[item.id];
    if (state !== undefined) {
      return state;
    }

    return item.status === 'queued' || item.status === 'running';
  }

  protected async openItem(itemId: string): Promise<void> {
    await this.notificationCenter.openItem(itemId);
  }

  protected async cancelTask(itemId: string): Promise<void> {
    await this.notificationCenter.cancelTask(itemId);
  }

  protected remove(itemId: string): void {
    this.notificationCenter.remove(itemId);
  }

  protected dismiss(itemId: string): void {
    this.notificationCenter.dismiss(itemId);
  }

  protected canCancel(item: NotificationItem): boolean {
    return item.type === 'task' && (item.status === 'queued' || item.status === 'running') && item.cancellable;
  }

  protected canDismiss(item: NotificationItem): boolean {
    return item.type === 'task' && (item.status === 'queued' || item.status === 'running') && !item.cancellable && item.dismissible;
  }

  protected canRemove(item: NotificationItem): boolean {
    if (!item.dismissible) {
      return false;
    }

    if (item.type === 'simple') {
      return true;
    }

    return item.status === 'success' || item.status === 'error' || item.status === 'canceled';
  }

  protected getItemIcon(item: NotificationItem): string {
    switch (item.kind) {
      case 'upload':
        return 'upload';
      case 'report':
        return 'description';
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'notifications';
    }
  }

  protected getStatusLabel(item: NotificationItem): string {
    if (item.type === 'simple') {
      return 'Notification';
    }

    switch (item.status) {
      case 'queued':
        return 'Queued';
      case 'running':
        return 'In progress';
      case 'success':
        return 'Recent';
      case 'error':
        return 'Needs attention';
      case 'canceled':
        return 'Canceled';
    }
  }

  protected getStatusClasses(item: NotificationItem): string {
    if (item.type === 'simple') {
      return 'bg-slate-100 text-slate-700';
    }

    switch (item.status) {
      case 'queued':
        return 'bg-amber-100 text-amber-700';
      case 'running':
        return 'bg-blue-100 text-blue-700';
      case 'success':
        return 'bg-emerald-100 text-emerald-700';
      case 'error':
        return 'bg-rose-100 text-rose-700';
      case 'canceled':
        return 'bg-slate-200 text-slate-700';
    }
  }

  protected getProgressPercent(item: NotificationItem): number {
    if (item.type !== 'task') {
      return 0;
    }

    return Math.max(0, Math.min(item.progress?.percent ?? 0, 100));
  }

  protected hasEntries(item: NotificationItem): item is TaskNotification {
    return item.type === 'task' && !!item.entries?.length;
  }

  protected hasOpenAction(item: NotificationItem): boolean {
    return !!item.route || (item.type === 'task' && !!item.actionLabel);
  }

  protected isOpenActionDisabled(item: NotificationItem): boolean {
    return item.type === 'task' && !!item.actionDisabled;
  }

  protected getOpenActionTooltip(item: NotificationItem): string | undefined {
    if (item.type !== 'task' || !item.actionDisabled) {
      return undefined;
    }

    return item.actionTooltip;
  }

  protected getOpenLabel(item: NotificationItem): string {
    if (item.type === 'task') {
      return item.actionLabel ?? 'Open';
    }

    return 'Open';
  }

  protected trackById(_: number, item: NotificationItem): string {
    return item.id;
  }
}
